import React, { useState, useEffect } from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { IBaseProduct } from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { AppMainView } from '../../../components/AppView'
import AppHeader from '../../../components/AppView/Header'
import Helmet from 'react-helmet'
import { DatePicker } from 'antd'
import { Button, message } from 'antd-v4'
import moment from 'moment'
import { HelpLinkMenu } from '../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { BlazeCRSALocationSelector, ICRSALocations } from './BlazeCRSALocationSelector'
import { ILocationContract } from '@getgreenline/locations/build/models'
import { AsyncProductInput } from '../../../components/GrInput/AsyncProductInput'
import { CloseOutlined } from '@ant-design/icons'
import {
  ITenantHashKeyPathParams,
  ICreateResyncRequestContract,
  createResyncProducts,
} from '@getgreenline/ocs'
import Endpoints from '../../../constants/Endpoints'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'
import BlazeTable from '../../../components/CustomTableV2/BlazeTable'
import './OCSComplianceDataReconciliation.scss'
import { HelpLinks } from '../../../constants/ExternalLinks'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

const DataReconciliationComponent = ({ currentCompanyStore, currentUserStore }: Props) => {
  const [selectedCRSA, setSelectedCRSA] = useState<ICRSALocations | undefined>(undefined)
  const [locationsWithCRSA, setLocationsWithCRSA] = useState<ILocationContract[]>([])
  const [hashKeys, setHashKeys] = useState<Map<string | undefined, string>>(new Map())
  const [addedProducts, setAddedProducts] = useState<Map<string, IBaseProduct>>(new Map())
  const [startDates, setStartDates] = useState<
    Map<string, { startDate: Date | null; startDateString: string }>
  >(new Map())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const maxProductCount = 25
  const hasReachedMaxProductCount = addedProducts.size >= maxProductCount
  const hasSelectedCRSA = !!selectedCRSA
  const hasAddedProducts = addedProducts.size > 0
  const hasSelectedStartDates = startDates.size === addedProducts.size
  const shouldDisableSubmit = !hasSelectedCRSA || !hasAddedProducts || !hasSelectedStartDates
  useEffect(() => {
    setLocationsWithCRSA(getLocationsWithCRSA(currentCompanyStore?.locations))
    setHashKeys(getHashKeys())
  }, [currentCompanyStore?.globalHashKeys])

  const getDisabledReasonText = () => {
    if (!hasSelectedCRSA) return 'Please select a CRSA number'
    if (!hasAddedProducts) return 'Please add a product'
    if (!hasSelectedStartDates) return 'Please select a start date for each product'
    return ''
  }

  const getLocationsWithCRSA = (locations: ILocationContract[] | undefined) => {
    const crsaRegex = /^CRSA[0-9]{7}$/

    const isValidCRSALocationFilter = (location: ILocationContract) =>
      crsaRegex.test(location.storeId || '')

    const locationsWithCRSA: ILocationContract[] = locations
      ? locations.filter(isValidCRSALocationFilter)
      : []

    return locationsWithCRSA
  }

  const getHashKeys = () => {
    const hashKeys = new Map(
      currentCompanyStore?.globalHashKeys?.map(({ retailerCRSA, hashKey }) => [
        retailerCRSA,
        hashKey,
      ]),
    )

    return hashKeys
  }

  const addProducts = (products: IBaseProduct[]) => {
    setAddedProducts((prevMap) => {
      const newMap = new Map(prevMap)
      products.forEach((product) => {
        newMap.set(product.id, product)
      })
      return newMap
    })
  }

  const deleteProduct = (productID: string) => {
    setAddedProducts((prevMap) => {
      const newMap = new Map(prevMap)
      newMap.delete(productID)
      return newMap
    })
  }

  const deleteStartDate = (productID: string) => {
    setStartDates((prevMap) => {
      const newMap = new Map(prevMap)
      newMap.delete(productID)
      return newMap
    })
  }

  const updateStartDates = (product: IBaseProduct, date: Date | null, dateString: string) => {
    setStartDates((prevMap) => {
      const newMap = new Map(prevMap)
      newMap.set(product.id, { startDate: date, startDateString: dateString })
      return newMap
    })
  }

  const submit = async () => {
    setIsSubmitting(true)

    const hashKey = hashKeys.get(selectedCRSA?.crsa)

    const tenantHashKeyPathParams: ITenantHashKeyPathParams = {
      gateway: Endpoints.AUTHZ_GATEWAY_URL,
      tenantId: `${currentCompanyStore?.company?.id}`,
      hashKey: hashKey ?? '',
    }

    const endDate = new Date().toISOString()

    const currentUserID: string | undefined = currentUserStore?.currentUser?.id

    const createPayload = (
      startDates: Map<string, { startDate: Date | null; startDateString: string }>,
    ) => {
      const payload: ICreateResyncRequestContract[] = []

      for (const [id, startDateData] of startDates.entries()) {
        const resyncProduct: ICreateResyncRequestContract = {
          productId: id,
          rangeStart: startDateData.startDate?.toISOString() ?? '',
          rangeEnd: endDate,
          updateBy: currentUserID ? `userId-${currentUserID}` : '',
        }
        payload.push(resyncProduct)
      }

      return payload
    }

    const payload = createPayload(startDates)

    try {
      if (!hashKey) {
        throw 'no hashkey associated with this CRSA number'
      }
      await createResyncProducts(tenantHashKeyPathParams, payload)
      message.info('Data reconciliation was successfully submitted')
      setAddedProducts(new Map<string, IBaseProduct>())
      setStartDates(new Map<string, { startDate: Date | null; startDateString: string }>())
    } catch (error) {
      console.error(error)
      message.error(
        'Failed to submit data reconciliation. Please try again or contact support for assistance.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const startDateColumnHelper = (product: IBaseProduct) => {
    const onChange = (date: moment.Moment | null, dateString: string) => {
      if (date === null) {
        deleteStartDate(product.id)
      } else {
        const vanillaDate = date.toDate()
        updateStartDates(product, vanillaDate, dateString)
      }
    }
    return (
      <DatePicker
        className='ocs-data-reconciliation-date-picker'
        onChange={onChange}
        value={
          startDates.get(product.id)?.startDate
            ? moment(startDates.get(product.id)?.startDate || undefined)
            : undefined
        }
      />
    )
  }

  const removeColumnHelper = (product: IBaseProduct) => {
    const deleteRow = () => {
      deleteProduct(product.id)
      deleteStartDate(product.id)
    }
    return <Button icon={<CloseOutlined />} size='small' onClick={deleteRow} />
  }

  const columns = [
    {
      key: 'name',
      title: 'Product Name',
      dataIndex: 'name',
      width: '35%',
    },
    {
      key: 'sku',
      title: 'SKU',
      dataIndex: 'sku',
      width: '18%',
    },
    {
      key: 'barcode',
      title: 'Barcode',
      dataIndex: 'barcode',
      width: '25%',
    },
    {
      key: 'category',
      title: 'Category',
      dataIndex: 'categoryName',
      width: '18%',
    },
    {
      key: 'startDate',
      title: 'POS Transaction Date',
      width: 170,
      render: startDateColumnHelper,
    },
    {
      key: 'remove',
      title: 'Remove',
      align: 'center' as const,
      width: 80,
      render: removeColumnHelper,
    },
  ]

  return (
    <div className='d-flex flex-column h-100'>
      <Helmet>
        <title>Compliance | Data Reconciliation</title>
        <meta name='page-loaded' content='true' />
      </Helmet>
      <AppHeader title='Data Reconciliation'>
        <HelpLinkMenu
          helpLinkMenus={{
            title: 'Compliance',
            href: HelpLinks.REPORTS_COMPLIANCE,
          }}
        />
      </AppHeader>

      <AppMainView className='background-grey intercom-pb-2'>
        <div className='white-rounded-container' style={{ paddingBottom: 0 }}>
          <p>
            Please, select all the products that you updated based on your Data Reconciliation
            e-mail.
            <br /> For each product, select the corresponding start date (data will be reconciled
            from the start date to today).
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            <BlazeCRSALocationSelector
              locations={locationsWithCRSA}
              onSelect={({ crsaLocation }) => setSelectedCRSA(crsaLocation)}
            />

            <AsyncProductInput
              showSearch
              disabled={hasReachedMaxProductCount}
              tooltipText={
                hasReachedMaxProductCount
                  ? `Please submit no more than ${maxProductCount} products`
                  : ''
              }
              mode='default'
              placeholder='Add a product'
              className='w-50'
              allowClear
              productIdsToHide={Array.from(addedProducts.keys()) || ''}
              onChange={(products) => addProducts(products)}
            />
          </div>
        </div>

        <BlazeTable
          rowKey={(record) => record.id}
          data={Array.from(addedProducts.values())}
          columns={columns}
          emptyDescription='No products added yet'
          className='ocs-compliance-data-reconciliation-table'
          antdPagination={{
            defaultPageSize: 10,
            total: addedProducts.size,
            showTotal: (total, range) => `${range[1]} of ${total} items`,
          }}
          tableProps={{ scroll: { x: '100%' } }}
        />

        <div style={{ paddingTop: '1em', textAlign: 'center' }}>
          <BlazeButton
            buttonProps={{
              type: 'primary',
              size: 'large',
              disabled: shouldDisableSubmit,
              htmlType: 'submit',
              onClick: submit,
              loading: isSubmitting,
            }}
            titleProps={{
              title: isSubmitting ? '' : 'Submit',
            }}
            tooltipText={getDisabledReasonText()}
          />
        </div>
      </AppMainView>
    </div>
  )
}

export const DataReconciliation = inject(
  'currentUserStore',
  'currentCompanyStore',
)(observer(DataReconciliationComponent))
